import { useEffect, useState } from "react";
import { X, Download, Loader2 } from "lucide-react";

/**
 * Modale d'aperçu PDF (rendu en image pour compatibilité navigateur).
 * Props:
 *   previewUrl : string — URL de l'endpoint qui retourne l'image PNG
 *   downloadUrl: string — URL de l'endpoint qui retourne le PDF à télécharger
 *   seq        : string — numéro de séquence (pour nom du fichier)
 *   onClose    : fn     — fermer la modale
 */
export default function PDFPreviewModal({ previewUrl, downloadUrl, seq, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fermer avec Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleDownload = async () => {
    try {
      const res = await fetch(downloadUrl);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DAJ_${seq}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Erreur lors du téléchargement.");
    }
  };

  return (
    <div
      className="pdf-preview-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="pdf-preview-modal"
    >
      <div className="pdf-preview-container">
        {/* Barre d'outils */}
        <div className="pdf-preview-toolbar">
          <span className="pdf-preview-title">
            Aperçu — Demande d'aide juridique&nbsp;
            <strong>{seq}</strong>
          </span>
          <div className="pdf-preview-actions">
            <button
              className="pdf-btn pdf-btn-download"
              onClick={handleDownload}
              title="Télécharger le PDF"
              data-testid="pdf-download-btn"
            >
              <Download size={16} />
              <span>Télécharger</span>
            </button>
            <button
              className="pdf-btn pdf-btn-close"
              onClick={onClose}
              title="Fermer"
              data-testid="pdf-close-btn"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Visionneuse - Image PNG */}
        <div className="pdf-preview-viewer">
          {loading && (
            <div className="pdf-loading">
              <Loader2 className="animate-spin" size={32} />
              <span>Chargement de l'aperçu...</span>
            </div>
          )}
          {error && (
            <div className="pdf-error">
              <span>Erreur: {error}</span>
            </div>
          )}
          <img
            src={previewUrl}
            alt={`Aperçu DAJ ${seq}`}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError("Impossible de charger l'aperçu");
            }}
            style={{
              display: loading ? "none" : "block",
              maxWidth: "100%",
              height: "auto",
              margin: "0 auto"
            }}
            data-testid="pdf-preview-image"
          />
        </div>
      </div>
    </div>
  );
}
